import type {
  NoticeBlock,
  NoticeColumn,
  NoticeComposition,
  NoticeRun,
} from './notice-fidelity.models';

export interface NoticeCompositionChange {
  id: string;
  group: string;
  label: string;
  before: string;
  after: string;
  blockId?: string;
}

export interface NoticeCompositionComparison {
  changes: NoticeCompositionChange[];
  compatible: boolean;
  message?: string;
}

type RecordValue = Record<string, unknown>;
type Display = (value: unknown) => string;
type Property = readonly [key: string, label: string, display?: Display];

const pageProperties: Property[] = [
  [
    'size',
    'Paper size',
    (value) =>
      value === 'letter'
        ? 'Letter portrait'
        : value === 'a4'
          ? 'A4 portrait'
          : text(value),
  ],
  ['marginPt', 'Page margin', points],
  ['fontCode', 'Body font'],
  ['fontSizePt', 'Body font size', points],
  ['lineHeight', 'Body line height', (value) => `${text(value)} ×`],
];
const brandingProperties: Property[] = [
  [
    'profile',
    'Brand profile version',
    (value) =>
      value == null ? 'Local document branding' : JSON.stringify(value),
  ],
  ['headerText', 'Header wording', wording],
  ['footerText', 'Footer wording', wording],
  ['accentColor', 'Accent color', color],
  ['logoCode', 'Logo'],
  ['watermarkCode', 'Watermark'],
];
const styleProperties: Property[] = [
  ['fontSizePt', 'Font size', points],
  ['color', 'Text color', color],
  ['backgroundColor', 'Fill color', color],
  ['borderColor', 'Border color', color],
  ['borderWidthPt', 'Border width', points],
  ['paddingPt', 'Padding', points],
  [
    'keepTogether',
    'Keep together',
    (value) => (value === true ? 'Yes' : value === false ? 'No' : text(value)),
  ],
];
const columnProperties: Property[] = [
  ['label', 'Label', wording],
  ['field', 'Data field', (value) => `Field: ${text(value)}`],
  ['format', 'Format', format],
  ['width', 'Relative width', (value) => `${text(value)} weight`],
];

/** A display-only comparison of trusted structured sources. It never rewrites either source. */
export function compareNoticeCompositions(
  before: NoticeComposition,
  after: NoticeComposition,
): NoticeCompositionComparison {
  const changes: NoticeCompositionChange[] = [];
  const problems: string[] = [];
  const add = (
    id: string,
    group: string,
    label: string,
    oldValue: unknown,
    newValue: unknown,
    display: Display = text,
    blockId?: string,
  ) => {
    if (equal(oldValue, newValue)) return;
    changes.push({
      id,
      group,
      label,
      before: display(oldValue),
      after: display(newValue),
      ...(blockId === undefined ? {} : { blockId }),
    });
  };
  const incompatible = (message: string) => {
    if (!problems.includes(message)) problems.push(message);
  };
  const properties = (
    oldValue: unknown,
    newValue: unknown,
    fields: Property[],
    path: string,
    group: string,
    blockId?: string,
  ) => {
    const oldRecord = record(oldValue);
    const newRecord = record(newValue);
    for (const [key, label, display] of fields)
      add(
        `${path}.${key}`,
        group,
        label,
        oldRecord[key],
        newRecord[key],
        display,
        blockId,
      );
    technical(
      oldRecord,
      newRecord,
      fields.map(([key]) => key),
      path,
      group,
      blockId,
    );
  };
  const technical = (
    oldValue: RecordValue,
    newValue: RecordValue,
    known: string[],
    path: string,
    group: string,
    blockId?: string,
  ) => {
    const keys = [
      ...new Set([...Object.keys(oldValue), ...Object.keys(newValue)]),
    ].sort();
    for (const key of keys) {
      if (known.includes(key)) continue;
      add(
        `${path}[${JSON.stringify(key)}]`,
        group,
        `Retained property: ${key}`,
        oldValue[key],
        newValue[key],
        technicalText,
        blockId,
      );
    }
  };
  const identity = (
    oldValue: unknown,
    newValue: unknown,
    path: string,
    label: string,
  ) => {
    add(path, 'Document identity', label, oldValue, newValue, technicalText);
    if (!equal(oldValue, newValue))
      incompatible(
        'The document contract or registered context changed. These sources do not share a comparable rendering contract.',
      );
  };

  if (!isRecord(before) || !isRecord(after))
    return {
      changes: [
        {
          id: 'document.invalid',
          group: 'Document identity',
          label: 'Structured source unavailable',
          before: technicalText(before),
          after: technicalText(after),
        },
      ],
      compatible: false,
      message: 'Both sources must be structured notice compositions.',
    };

  identity(
    before.contractCode,
    after.contractCode,
    'document.contractCode',
    'Document contract',
  );
  identity(
    before.version,
    after.version,
    'document.version',
    'Document contract version',
  );
  identity(
    before.context,
    after.context,
    'document.context',
    'Registered context',
  );
  if (!supported(before) || !supported(after)) {
    incompatible(
      'A source uses an unsupported document contract or version. Its retained changes are shown for inspection only.',
    );
    if (
      equal(before.contractCode, after.contractCode) &&
      equal(before.version, after.version)
    )
      changes.push({
        id: 'document.unsupported',
        group: 'Document identity',
        label: 'Unsupported document contract',
        before: `${text(before.contractCode)} v${text(before.version)}`,
        after: `${text(after.contractCode)} v${text(after.version)}`,
      });
  }
  properties(before.page, after.page, pageProperties, 'page', 'Page setup');
  properties(
    before.branding,
    after.branding,
    brandingProperties,
    'branding',
    'Branding',
  );
  technical(
    record(before),
    record(after),
    ['contractCode', 'version', 'context', 'page', 'branding', 'blocks'],
    'document',
    'Document retained properties',
  );
  if (
    !isRecord(before.page) ||
    !isRecord(after.page) ||
    !isRecord(before.branding) ||
    !isRecord(after.branding)
  )
    incompatible(
      'Page or branding settings are malformed. Use validated structured sources before comparing proofs.',
    );

  const beforeBlocks = validIdentities(before.blocks);
  const afterBlocks = validIdentities(after.blocks);
  if (!beforeBlocks || !afterBlocks) {
    incompatible(
      'Block identities are missing or ambiguous. Blocks were not paired; validate both sources before comparison.',
    );
    changes.push({
      id: 'blocks.identities',
      group: 'Document structure',
      label: 'Block identities cannot be compared',
      before: beforeBlocks ? 'Unique block IDs' : technicalText(before.blocks),
      after: afterBlocks ? 'Unique block IDs' : technicalText(after.blocks),
    });
  } else {
    const oldBlocks = before.blocks;
    const newBlocks = after.blocks;
    if ([...oldBlocks, ...newBlocks].some((block) => !knownBlock(block)))
      incompatible(
        'A source contains an unsupported block type. Its retained changes are shown for inspection only.',
      );
    if (
      [...oldBlocks, ...newBlocks].some(
        (block) =>
          ['paragraph', 'box'].includes(block.kind) &&
          (!Array.isArray(block.runs) || !validRuns(block.runs)),
      )
    )
      incompatible(
        'An inline source is malformed. Validate both sources before comparison.',
      );
    if (
      [...oldBlocks, ...newBlocks].some(
        (block) => block.kind !== 'pageBreak' && !isRecord(block.style),
      )
    )
      incompatible(
        'Block appearance is malformed. Validate both sources before comparison.',
      );
    if (
      [...oldBlocks, ...newBlocks].some(
        (block) => block.kind === 'repeat' && !validIdentities(block.columns),
      )
    )
      incompatible(
        'Table column identities are missing or ambiguous. Columns were not paired; validate both sources before comparison.',
      );
    // Relative order of retained IDs avoids presenting insertion/removal shifts as moves.
    const common = new Set(
      oldBlocks
        .map((block) => block.id)
        .filter((id) => newBlocks.some((block) => block.id === id)),
    );
    add(
      'blocks.order',
      'Document structure',
      'Block reading order',
      oldBlocks
        .filter((block) => common.has(block.id))
        .map((block) => block.id),
      newBlocks
        .filter((block) => common.has(block.id))
        .map((block) => block.id),
      order,
    );
    for (const oldBlock of oldBlocks) {
      if (!newBlocks.some((block) => block.id === oldBlock.id)) {
        add(
          `blocks.${oldBlock.id}.removed`,
          'Document structure',
          'Block removed',
          blockSummary(oldBlock),
          undefined,
          summaryText,
        );
        retainedBlock(oldBlock, undefined);
      }
    }
    for (let index = 0; index < newBlocks.length; index++) {
      const block = newBlocks[index];
      const oldBlock = oldBlocks.find((candidate) => candidate.id === block.id);
      if (!oldBlock) {
        add(
          `blocks.${block.id}.added`,
          'Document structure',
          `Block added at position ${index + 1}`,
          undefined,
          blockSummary(block),
          summaryText,
          block.id,
        );
        retainedBlock(undefined, block);
        continue;
      }
      const path = `blocks.${block.id}`;
      const group = blockIdentity(block);
      add(
        `${path}.kind`,
        group,
        'Block type',
        oldBlock.kind,
        block.kind,
        text,
        block.id,
      );
      if (!equal(oldBlock.runs, block.runs)) {
        if (validRuns(oldBlock.runs) && validRuns(block.runs)) {
          add(
            `${path}.runs`,
            group,
            'Wording, references and emphasis',
            normalizeRuns(oldBlock.runs),
            normalizeRuns(block.runs),
            runsText,
            block.id,
          );
          // Run metadata remains observable even when familiar literal segments normalize.
          const oldExtras = runExtras(oldBlock.runs);
          const newExtras = runExtras(block.runs);
          add(
            `${path}.runs.retained`,
            group,
            'Retained inline properties',
            oldExtras,
            newExtras,
            technicalText,
            block.id,
          );
        } else {
          add(
            `${path}.runs`,
            group,
            'Inline source cannot be compared',
            oldBlock.runs,
            block.runs,
            technicalText,
            block.id,
          );
          incompatible(
            'An inline source is malformed. Validate both sources before comparison.',
          );
        }
      }
      properties(
        oldBlock.style,
        block.style,
        styleProperties,
        `${path}.style`,
        `${group} · Appearance`,
        block.id,
      );
      add(
        `${path}.source`,
        group,
        'Repeating data source',
        oldBlock.source,
        block.source,
        (value) => (value === undefined ? 'Not set' : `List: ${text(value)}`),
        block.id,
      );
      compareColumns(oldBlock, block, path, group);
      technical(
        record(oldBlock),
        record(block),
        ['id', 'kind', 'runs', 'style', 'source', 'columns'],
        path,
        group,
        block.id,
      );
    }
  }
  return {
    changes,
    compatible: problems.length === 0,
    ...(problems.length ? { message: problems.join(' ') } : {}),
  };

  function compareColumns(
    oldBlock: NoticeBlock,
    block: NoticeBlock,
    path: string,
    group: string,
  ): void {
    if (oldBlock.columns === undefined && block.columns === undefined) return;
    const oldColumns = oldBlock.columns === undefined ? [] : oldBlock.columns;
    const newColumns = block.columns === undefined ? [] : block.columns;
    if (!validIdentities(oldColumns) || !validIdentities(newColumns)) {
      incompatible(
        'Table column identities are missing or ambiguous. Columns were not paired; validate both sources before comparison.',
      );
      changes.push({
        id: `${path}.columns.identities`,
        group,
        label: 'Column identities cannot be compared',
        before: technicalText(oldBlock.columns),
        after: technicalText(block.columns),
        blockId: block.id,
      });
      return;
    }
    const common = new Set(
      oldColumns
        .map((column) => column.id)
        .filter((id) => newColumns.some((column) => column.id === id)),
    );
    add(
      `${path}.columns.order`,
      group,
      'Column order',
      oldColumns.filter((column) => common.has(column.id)).map(columnIdentity),
      newColumns.filter((column) => common.has(column.id)).map(columnIdentity),
      order,
      block.id,
    );
    for (const column of oldColumns) {
      if (!newColumns.some((candidate) => candidate.id === column.id)) {
        add(
          `${path}.columns.${column.id}.removed`,
          group,
          'Column removed',
          columnSummary(column),
          undefined,
          summaryText,
          block.id,
        );
        technical(
          record(column),
          {},
          ['id', ...columnProperties.map(([key]) => key)],
          `${path}.columns.${column.id}`,
          `${group} · ${columnIdentity(column)}`,
          block.id,
        );
      }
    }
    for (let index = 0; index < newColumns.length; index++) {
      const column = newColumns[index];
      const oldColumn = oldColumns.find(
        (candidate) => candidate.id === column.id,
      );
      const columnPath = `${path}.columns.${column.id}`;
      if (!oldColumn) {
        add(
          `${columnPath}.added`,
          group,
          `Column added at position ${index + 1}`,
          undefined,
          columnSummary(column),
          summaryText,
          block.id,
        );
        technical(
          {},
          record(column),
          ['id', ...columnProperties.map(([key]) => key)],
          columnPath,
          `${group} · ${columnIdentity(column)}`,
          block.id,
        );
        continue;
      }
      properties(
        oldColumn,
        column,
        columnProperties,
        columnPath,
        `${group} · ${columnIdentity(column)}`,
        block.id,
      );
    }
  }

  function retainedBlock(
    oldBlock: NoticeBlock | undefined,
    block: NoticeBlock | undefined,
  ): void {
    const identity = block ?? oldBlock!;
    const path = `blocks.${identity.id}`;
    const group = blockIdentity(identity);
    technical(
      record(oldBlock),
      record(block),
      ['id', 'kind', 'runs', 'style', 'source', 'columns'],
      path,
      group,
      block?.id,
    );
    technical(
      record(oldBlock?.style),
      record(block?.style),
      styleProperties.map(([key]) => key),
      `${path}.style`,
      `${group} · Appearance`,
      block?.id,
    );
    add(
      `${path}.runs.retained`,
      group,
      'Retained inline properties',
      runExtras(oldBlock?.runs),
      runExtras(block?.runs),
      technicalText,
      block?.id,
    );
    const columns = identity.columns;
    if (validIdentities(columns))
      for (const column of columns ?? [])
        technical(
          oldBlock ? record(column) : {},
          block ? record(column) : {},
          ['id', ...columnProperties.map(([key]) => key)],
          `${path}.columns.${column.id}`,
          `${group} · ${columnIdentity(column)}`,
          block?.id,
        );
  }
}

function record(value: unknown): RecordValue {
  return isRecord(value) ? value : {};
}
function isRecord(value: unknown): value is RecordValue {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
function supported(document: NoticeComposition): boolean {
  return (
    ['notice-fidelity-experimental', 'notice-document-context-proof'].includes(
      document.contractCode,
    ) && document.version === 1
  );
}
function knownBlock(block: NoticeBlock): boolean {
  return ['paragraph', 'box', 'repeat', 'pageBreak'].includes(block.kind);
}
function validIdentities(value: unknown): value is { id: string }[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        isRecord(item) &&
        typeof item['id'] === 'string' &&
        /^[a-z][a-z0-9_-]{0,47}$/.test(item['id']),
    ) &&
    new Set(value.map((item: { id: string }) => item.id)).size === value.length
  );
}
function validRuns(value: unknown): value is NoticeRun[] | undefined {
  return (
    value === undefined ||
    (Array.isArray(value) &&
      value.every(
        (run) =>
          isRecord(run) &&
          ['literal', 'field', 'term'].includes(run['kind'] as string) &&
          typeof run['value'] === 'string' &&
          typeof run['bold'] === 'boolean' &&
          typeof run['italic'] === 'boolean' &&
          (run['format'] === undefined || typeof run['format'] === 'string'),
      ))
  );
}
function normalizeRuns(runs: NoticeRun[] | undefined): NoticeRun[] {
  const normalized: NoticeRun[] = [];
  for (const run of runs ?? []) {
    const next = {
      kind: run.kind,
      value: run.value,
      bold: run.bold,
      italic: run.italic,
      ...(run.format === undefined ? {} : { format: run.format }),
    };
    const previous = normalized.at(-1);
    if (next.kind === 'literal' && next.format === undefined && !next.value)
      continue;
    if (
      previous?.kind === 'literal' &&
      next.kind === 'literal' &&
      previous.format === undefined &&
      next.format === undefined &&
      previous.bold === next.bold &&
      previous.italic === next.italic
    )
      previous.value += next.value;
    else normalized.push(next);
  }
  return normalized;
}
function runExtras(runs: unknown): unknown[] {
  if (!Array.isArray(runs)) return [];
  return runs.flatMap((run: unknown, index): unknown[] => {
    if (!isRecord(run)) return [{ runIndex: index, malformed: run }];
    const extras = Object.fromEntries(
      Object.entries(run).filter(
        ([key]) => !['kind', 'value', 'bold', 'italic', 'format'].includes(key),
      ),
    );
    return Object.keys(extras).length
      ? [{ runIndex: index, properties: extras }]
      : [];
  });
}
function runsText(value: unknown): string {
  const runs = value as NoticeRun[];
  if (!runs.length) return 'No inline content';
  return runs
    .map(
      (run) =>
        `${run.kind === 'literal' ? 'Text' : run.kind === 'field' ? 'Field' : 'Terminology'}: ${wording(run.value)}${run.kind !== 'literal' || run.format !== undefined ? ` · ${format(run.format)}` : ''}${run.bold ? ' · bold' : ''}${run.italic ? ' · italic' : ''}`,
    )
    .join('\n');
}
function blockIdentity(block: NoticeBlock): string {
  return `${block.kind === 'paragraph' ? 'Paragraph' : block.kind === 'box' ? 'Box' : block.kind === 'repeat' ? 'Table' : block.kind === 'pageBreak' ? 'Page break' : 'Block'}: ${block.id}`;
}
function columnIdentity(column: NoticeColumn): string {
  return `Column: ${column.id}`;
}
function blockSummary(block: NoticeBlock): string {
  const parts = [blockIdentity(block)];
  if (validRuns(block.runs) && block.runs !== undefined)
    parts.push(runsText(normalizeRuns(block.runs)));
  else if (block.runs !== undefined)
    parts.push(`Inline source unavailable: ${technicalText(block.runs)}`);
  if (block.source !== undefined) parts.push(`List: ${block.source}`);
  if (validIdentities(block.columns))
    parts.push(...block.columns.map(columnSummary));
  else if (block.columns !== undefined)
    parts.push(`Column source unavailable: ${technicalText(block.columns)}`);
  if (isRecord(block.style))
    parts.push(
      ...styleProperties
        .filter(([key]) => Object.hasOwn(block.style!, key))
        .map(
          ([key, label, display = text]) =>
            `${label}: ${display(record(block.style)[key])}`,
        ),
    );
  const retained = retainedProperties(record(block), [
    'id',
    'kind',
    'runs',
    'style',
    'source',
    'columns',
  ]);
  const retainedStyle = retainedProperties(
    record(block.style),
    styleProperties.map(([key]) => key),
  );
  if (Object.keys(retained).length)
    parts.push(`Retained properties: ${technicalText(retained)}`);
  if (Object.keys(retainedStyle).length)
    parts.push(`Retained style properties: ${technicalText(retainedStyle)}`);
  if (runExtras(block.runs).length)
    parts.push(
      `Retained inline properties: ${technicalText(runExtras(block.runs))}`,
    );
  return parts.join('\n');
}
function columnSummary(column: NoticeColumn): string {
  const retained = retainedProperties(record(column), [
    'id',
    'label',
    'field',
    'format',
    'width',
  ]);
  return `${columnIdentity(column)} · ${wording(column.label)} · Field: ${text(column.field)} · ${format(column.format)} · ${text(column.width)} weight${Object.keys(retained).length ? `\nRetained properties: ${technicalText(retained)}` : ''}`;
}
function retainedProperties(value: RecordValue, known: string[]): RecordValue {
  return Object.fromEntries(
    Object.entries(value).filter(([key]) => !known.includes(key)),
  );
}
function text(value: unknown): string {
  return value === undefined
    ? 'Not set'
    : typeof value === 'string'
      ? value || '(empty)'
      : technicalText(value);
}
function wording(value: unknown): string {
  return value === undefined
    ? 'Not set'
    : typeof value === 'string'
      ? value.length
        ? `“${value}”`
        : '(empty text)'
      : technicalText(value);
}
function points(value: unknown): string {
  return value === undefined ? 'Not set' : `${text(value)} pt`;
}
function color(value: unknown): string {
  return value === undefined ? 'Not set' : `Color: ${text(value)}`;
}
function format(value: unknown): string {
  return value === undefined
    ? 'Default format (omitted)'
    : `Explicit format: ${text(value)}`;
}
function summaryText(value: unknown): string {
  return value === undefined ? 'Absent' : text(value);
}
function order(value: unknown): string {
  return Array.isArray(value) && value.length
    ? value.join(' → ')
    : 'No retained items';
}
function technicalText(value: unknown): string {
  if (value === undefined) return 'Not set';
  const serialized = stable(value);
  return serialized ?? 'Unavailable retained value';
}
function equal(before: unknown, after: unknown): boolean {
  return stable(before) === stable(after);
}
function stable(value: unknown): string | undefined {
  try {
    return JSON.stringify(value, (_key, item: unknown) =>
      isRecord(item)
        ? Object.fromEntries(
            Object.entries(item).sort(([left], [right]) =>
              left.localeCompare(right),
            ),
          )
        : item,
    );
  } catch {
    return undefined;
  }
}
